import { useCallback } from 'react';

type TreslogSource = 'landing_page' | 'dashboard' | 'upsell';
type TreslogTrigger = 'post_announcement' | 'manual';
type LegacyTrigger = 'treslog_interest' | 'other';
type OnboardingVariant = 'full' | 'condensed';
type FounderEvent = 'founder_qualified' | 'founder_reload' | 'founder_expired' | 'founder_reminder_dismissed';

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
  timestamp: string;
}

// Simple analytics queue for future integration
const analyticsQueue: AnalyticsEvent[] = [];

function trackEvent(event: string, properties?: Record<string, unknown>) {
  const analyticsEvent: AnalyticsEvent = {
    event,
    properties,
    timestamp: new Date().toISOString(),
  };

  // Queue for future analytics service integration
  analyticsQueue.push(analyticsEvent);

  // Log in development for debugging
  if (import.meta.env.DEV) {
    console.log('[Analytics]', event, properties);
  }
}

export function useAnalytics() {
  // TRESLOG events
  const trackTreslogViewed = useCallback((source: TreslogSource) => {
    trackEvent('treslog_viewed', { source });
  }, []);

  const trackTreslogEntryStarted = useCallback((trigger: TreslogTrigger) => {
    trackEvent('treslog_entry_started', { trigger });
  }, []);

  const trackTreslogEntrySaved = useCallback((wordCount: number) => {
    trackEvent('treslog_entry_saved', { word_count: wordCount });
  }, []);

  const trackTreslogAiAssistUsed = useCallback((promptType: string) => {
    trackEvent('treslog_ai_assist_used', { prompt_type: promptType });
  }, []);

  // Legacy plan events
  const trackLegacyPlanViewed = useCallback((source: string) => {
    trackEvent('legacy_plan_viewed', { source });
  }, []);

  const trackLegacyPlanPurchased = useCallback((trigger: LegacyTrigger) => {
    trackEvent('legacy_plan_purchased', { trigger });
  }, []);

  // Onboarding A/B test events
  const trackOnboardingStarted = useCallback((variant: OnboardingVariant) => {
    trackEvent('onboarding_started', { variant, ab_test: 'onboarding_v1' });
  }, []);

  const trackOnboardingCompleted = useCallback((variant: OnboardingVariant, screensViewed: number, intent?: string) => {
    trackEvent('onboarding_completed', { 
      variant, 
      screens_viewed: screensViewed,
      intent,
      ab_test: 'onboarding_v1'
    });
  }, []);

  const trackOnboardingSkipped = useCallback((variant: OnboardingVariant, screen: number) => {
    trackEvent('onboarding_skipped', { variant, screen, ab_test: 'onboarding_v1' });
  }, []);

  const trackFirstAnnouncementPublished = useCallback((variant: OnboardingVariant, timeToPublishMs: number) => {
    trackEvent('first_announcement_published', { 
      variant, 
      time_to_publish_ms: timeToPublishMs,
      ab_test: 'onboarding_v1'
    });
  }, []);

  // Founder events
  const trackFounderEvent = useCallback((event: FounderEvent, properties?: Record<string, unknown>) => {
    trackEvent(`founder_${event}`, properties);
  }, []);

  // General events
  const trackEvent$ = useCallback((event: string, properties?: Record<string, unknown>) => {
    trackEvent(event, properties);
  }, []);

  return {
    trackTreslogViewed,
    trackTreslogEntryStarted,
    trackTreslogEntrySaved,
    trackTreslogAiAssistUsed,
    trackLegacyPlanViewed,
    trackLegacyPlanPurchased,
    trackOnboardingStarted,
    trackOnboardingCompleted,
    trackOnboardingSkipped,
    trackFirstAnnouncementPublished,
    trackFounderEvent,
    trackEvent: trackEvent$,
  };
}

// Export for testing or direct access
export { analyticsQueue, trackEvent };
